import { ChangeDetectorRef, Component, ErrorHandler, inject, OnInit } from '@angular/core';
import { CLIPBOARD } from '../../dom';
import { ErrorReport, ErrorReportAdapter } from '../error-report';
import { ErrorReporterService } from './error-reporter.service';

@Component({
    selector: 'nom-error-reporter',
    templateUrl: './error-reporter.component.html',
    styleUrls: ['./error-reporter.component.scss'],
    standalone: false
})
export class ErrorReporterComponent implements OnInit {
  private readonly errorHandler = inject(ErrorHandler);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly clipboard: Clipboard = inject(CLIPBOARD);

  public readonly adapter = new ErrorReportAdapter();

  /**
   * The list of reports viewed by this component.
   */
  public reports: ErrorReport[] = [];

  /**
   * Whether this component should be running and available.
   */
  public active = false;

  /**
   * Whether the button should pulse indicating a new report. This manages an animation.
   */
  public pulse = false;

  /**
   * Whether the dialog is open.
   */
  public open = false;

  /**
   * When the clipboard can't be accessed directly, this provides the fallback text to copy.
   */
  public copyText: string | null = null;

  /**
   * How many reports are there?
   */
  public get count(): number {
    return this.reports.length ?? 0;
  }

  /**
   * If ErrorReporterService is provided as the ErrorHandler for the application, this will return the reporter.
   * Otherwise, it returns null.
   *
   * This being null means the reporter is not detected.
   */
  public get reporter(): ErrorReporterService | null {
    if (this.isReporter(this.errorHandler)) {
      return this.errorHandler as ErrorReporterService;
    }
    return null;
  }

  public ngOnInit(): void {
    if (!this.reporter) {
      // Error reporter is not available, do not initialise.
      this.active = false;
      return;
    }

    try {
      this.active = true;
      this.reporter.reportLog.subscribe(report => {
        this.pulse = true;
        this.addReport(report);
      });
    } catch (e) {
      console.error('ErrorReporterComponent could not initialise', e);
    }
  }

  public toggle(): void {
    this.open = !this.open;
    this.changed();
  }

  public dismissAll(): void {
    this.reports = [];
    this.open = false;
    this.changed();
  }

  public addReport(report: ErrorReport): void {
    this.reports.push(report);
    this.changed();
  }

  public removeReport(report: ErrorReport): void {
    const index = this.reports.indexOf(report);
    if (index >= 0) {
      this.reports.splice(index, 1);
    }

    if (this.reports.length === 0) {
      this.open = false;
    }

    this.changed();
  }

  /**
   * Copy the list of reports currently tracked by this component.
   * This excludes dismissed reports.
   */
  public copyAll(): void {
    const str = this.adapter.stringifyList(this.reports);
    this.copyToClipboard(str);
  }

  /**
   * Copy every report that's ever been made to the report service.
   * This includes reports that have been dismissed by the service.
   */
  public copyEverythingSinceForever(): void {
    if (this.reporter) {
      const str = this.adapter.stringifyList(this.reporter.reports);
      this.copyToClipboard(str);
    }
  }

  public animationEnd(event: AnimationEvent): void {
    if (event.animationName === 'pulse') {
      this.pulse = false;
    }
  }

  public dismissCopy(): void {
    this.copyText = null;
    this.changed();
  }

  private async copyToClipboard(str: string) {
    try {
      await this.clipboard.writeText(str);
    } catch {
      this.copyText = str;
      this.changed();
    }
  }

  /**
   * Trigger change detection on the reporter.
   *
   * Change detection might have stopped on account of an error, so we need to run it manually.
   */
  private changed(): void {
    this.cd.detectChanges();
  }

  private isReporter(service: ErrorHandler): boolean {
    return service instanceof ErrorReporterService;
  }
}
